
// vue
import {
  ref,
  reactive,
  defineComponent,
  onUnmounted,
  watch,
  computed,
  nextTick,
  watchEffect,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

// firebase
import 'firebase/firestore';
import { db, serverTimestamp } from '@/main';

// atoms
import Card from '@/components/atoms/Card.vue';
import ChatMsgBody from '@/components/atoms/ChatMsgBody.vue';
import CustomButton from '@/components/atoms/CustomButton.vue';
import TextField from '@/components/atoms/TextField.vue';
import MultilineField from '@/components/atoms/MultilineField.vue';
import SelectBox from '@/components/atoms/SelectBox.vue';

// functions
import {
  getChatPlaceholder,
  getFilterOption,
} from '@/functions/molecules/ChatArea';

// util
import {
  calcDiffTimeFromNow,
  convertDateToString,
  getUserRole,
  initList,
  initObj,
  initLastFetchDate,
} from '@/utils';

export default defineComponent({
  name: 'ChatArea',
  components: {
    Card,
    ChatMsgBody,
    CustomButton,
    TextField,
    MultilineField,
    SelectBox,
  },
  props: {
    roomId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const postUser = getUserRole(route);
    const chatPlaceHolder = ref(getChatPlaceholder(postUser));
    const chatArea = ref();

    const filterOption = ref(getFilterOption(postUser));
    const selectedFilterVal = ref('all');

    const id = props.roomId;
    const curQID = computed(() => store.state.curQuestionId);
    // TODO: 問題一覧を取得したら、adminページでどの問題に対してチャットを送るか設定できるようにする
    const questionIdList = computed(() => store.state.questionIdList);
    const questionObj = computed(() => store.state.questionObj);

    watchEffect(() => {
      // 現状講師画面でのみ問題ごとのチャット表示を可能にする
      if (postUser !== 'admin') return;

      const questionOptions = [];
      if (questionIdList.value.length > 0) {
        for (const questionId of questionIdList.value) {
          const option = {
            label: `${questionObj.value[questionId].body}のチャットを表示`,
            val: questionId,
          };

          questionOptions.push(option);
        }
      }

      filterOption.value = getFilterOption('admin', questionOptions);
    });

    // チャット
    const isSending = ref(false);
    const msgIdList = reactive(initList(id, 'msgIdList'));
    const msgObj = reactive(initObj(id, 'msgObj'));
    const lastMsgFetchDate = reactive(
      initLastFetchDate(id, 'lastMsgFetchDate')
    );
    const chatList = computed(() => {
      if (selectedFilterVal.value === 'all') {
        return msgIdList;
      }

      if (selectedFilterVal.value === 'current') {
        return msgIdList.filter((id) => {
          const postQId = msgObj[id]?.postQuestionId;
          // 該当の問題+adminのチャットを表示
          if (postQId === curQID.value || postQId === 'admin') {
            return id;
          }
        });
      }

      return msgIdList.filter((id) => {
        const postQId = msgObj[id]?.postQuestionId;
        if (postQId === selectedFilterVal.value || postQId === 'admin') {
          return id;
        }
      });
    });
    const user = computed(() => store.getters.getUser);

    const messagesUnsubscribe = ref<any>(null);
    const fetchChatMsg = () => {
      messagesUnsubscribe.value = db
        .collection('Room')
        .doc(id)
        .collection('messages')
        .where('createdAt', '>', lastMsgFetchDate)
        .orderBy('createdAt')
        .limit(10)
        .onSnapshot((snapShot) => {
          sessionStorage.setItem(`lastMsgFetchDate${id}`, String(new Date()));
          snapShot.forEach((doc) => {
            if (!msgIdList.includes(doc.id)) {
              msgIdList.push(doc.id);
            }

            msgObj[doc.id] = doc.data();
            msgObj[doc.id].createdAt = msgObj[doc.id].createdAt.toDate();
            msgObj[doc.id].updatedAt = msgObj[doc.id].updatedAt.toDate();
          });

          const tmpMsgObj = { ...msgObj };
          for (const msgObjKey of Object.keys(tmpMsgObj)) {
            tmpMsgObj[msgObjKey].createdAt = String(
              tmpMsgObj[msgObjKey].createdAt
            );
            tmpMsgObj[msgObjKey].updatedAt = String(
              tmpMsgObj[msgObjKey].updatedAt
            );
          }

          sessionStorage.setItem(`msgIdList${id}`, JSON.stringify(msgIdList));
          sessionStorage.setItem(`msgObj${id}`, JSON.stringify(tmpMsgObj));

          const clientHeight = chatArea.value.clientHeight;
          const scrollTop = chatArea.value.scrollTop;
          const scrollHeight = chatArea.value.scrollHeight;
          // スクロールが一番下（過去ログを読んでいない）ときは
          // 常にスクロールを一番下に動かす
          if (scrollHeight === clientHeight + scrollTop) {
            nextTick(() => {
              if (scrollHeight === clientHeight + scrollTop) {
                chatArea.value.scrollTop = chatArea.value.scrollHeight;
              }
            });
          }
        });
    };

    const isMsgFetched = ref(false);
    if (user?.value?.uid) {
      isMsgFetched.value = true;
      fetchChatMsg();
    } else {
      watch(user, () => {
        if (!user?.value?.uid || isMsgFetched.value) return;
        fetchChatMsg();
      });
    }

    const chatTxt = ref('');
    const sendMessage = () => {
      if (chatTxt.value.length === 0) return;
      if (isSending.value) return;
      isSending.value = true;

      const postQID =
        postUser === 'student' ? curQID.value || 'admin' : 'admin';

      db.collection('Room')
        .doc(id)
        .collection('messages')
        .add({
          body: chatTxt.value,
          postUser: postUser,
          postQuestionId: postQID,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        .then(() => {
          chatTxt.value = '';
        })
        .catch((e) => {
          console.error(e);
          alert(
            'メッセージの送信に失敗しました。\nお手数ですが、ブラウザをリロードした後に再度お試しください。'
          );
        })
        .finally(() => {
          isSending.value = false;
        });
    };

    onUnmounted(() => {
      messagesUnsubscribe.value();
    });

    return {
      chatArea,
      chatTxt,
      chatPlaceHolder,
      filterOption,
      selectedFilterVal,
      postUser,
      sendMessage,
      msgIdList,
      msgObj,
      calcDiffTimeFromNow,
      convertDateToString,
      chatList,
    };
  },
});
